import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { ApiError, getDevUserId, setDevUserId } from '../../api/client'
import { useAuthProviders, useDevUsers, useMe } from '../../api/hooks'
import { Spinner } from '../ui'

/** Ensures a principal exists before rendering the app.
 *
 *  - Logged in (session cookie, or dev header locally): render.
 *  - 401 and the API runs in local dev mode: pick the first project manager automatically.
 *  - 401 otherwise: send to the login page, remembering where the user wanted to go. */
export function Gate({ children }: { children: React.ReactNode }) {
  const me = useMe()
  const providers = useAuthProviders()
  const location = useLocation()
  const unauthorized = me.isError && (me.error as ApiError)?.status === 401
  const devMode = providers.data?.dev_switcher === true
  const users = useDevUsers(devMode && unauthorized)

  useEffect(() => {
    if (!devMode || !unauthorized || !users.data?.length) return
    const current = getDevUserId()
    if (!current || !users.data.some((u) => u.id === current)) {
      const pl = users.data.find((u) => u.role === 'PROJEKTLEDARE') ?? users.data[0]
      setDevUserId(pl.id)
      me.refetch()
    }
  }, [devMode, unauthorized, users.data, me])

  if (me.isLoading || (unauthorized && providers.isLoading) || (unauthorized && devMode && (users.isLoading || users.data?.length))) {
    return <Spinner className="min-h-dvh" />
  }
  if (unauthorized) {
    const next = location.pathname + location.search
    return <Navigate to={`/logga-in${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />
  }
  if (me.isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <div className="card max-w-md p-6 text-sm">
          <h1 className="mb-2 text-base">Kunde inte nå servern</h1>
          <p className="text-ink-500">API:t svarar inte. Lokalt: starta backend med <code>make api</code>.</p>
          <p className="mt-2 text-xs text-ink-400">{String((me.error as Error)?.message)}</p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}
